import { useMemo, Dispatch, SetStateAction, ReactElement } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { blockStates, type BlockDetails, type BlockState } from 'modules/campaign/block/types';
import { Toolbar } from './Toolbar';
import type { CampaignDetails } from '../types';
import type { EntityContextType } from 'components';
import { ActivityIcon } from 'components/Icon';
import { BlockGroup } from './BlockGroup';
import './CampaignOverview.scss';
import { Badge, Box, MenuList, MenuItem, Table, Th, Thead, Tr } from '@ff-it/ui';
import Big from 'big.js';
import { fmt, parseBig } from 'utilities';
import { activityTypes, type ActivityType } from 'types';
import { activityTypeLabels } from 'options';

type OverviewTab = ActivityType | 'all';

interface BlocksProps {
  blocks: BlockDetails[];
  setBlocks: Dispatch<SetStateAction<BlockDetails[] | undefined>>;
  ctx: EntityContextType<CampaignDetails>;
}

export function CampaignOverview({ blocks, ctx, setBlocks }: BlocksProps): ReactElement {
  const manage = ctx.item.role.manage;
  const { type: routeType } = useParams();
  const { pathname } = useLocation();
  const activeType: OverviewTab =
    routeType && (activityTypes as readonly string[]).includes(routeType) ? (routeType as ActivityType) : 'all';
  const segments = pathname.split('/');
  const planningBase = routeType ? segments.slice(0, -2).join('/') : pathname;

  const { add, remove, replace } = useMemo(
    () => ({
      add: (block: BlockDetails) => setBlocks((blocks) => [...(blocks as BlockDetails[]), block]),
      replace: (block: BlockDetails) =>
        setBlocks((blocks) => (blocks as BlockDetails[]).map((b) => (b.id === block.id ? block : b))),
      remove: (block: BlockDetails) =>
        setBlocks((blocks) => (blocks as BlockDetails[]).filter((b) => b.id !== block.id)),
    }),
    [setBlocks],
  );

  const typeCounts = useMemo(() => {
    const counts = {} as Record<ActivityType, number>;
    for (const block of blocks) counts[block.type] = (counts[block.type] ?? 0) + 1;
    return counts;
  }, [blocks]);

  const availableTypes = useMemo(() => activityTypes.filter((type) => typeCounts[type] > 0), [typeCounts]);

  const visibleBlocks = activeType === 'all' ? blocks : blocks.filter((b) => b.type === activeType);

  let budget = Big(0);
  let incomeTotal = Big(0);
  let expenseTotal = Big(0);
  let revenueTotal = Big(0);

  const groupedBlocks = visibleBlocks.reduce(
    (acc, block) => {
      if (block.state !== 'CANCELED') {
        budget = budget.plus(parseBig(block.budget));
        incomeTotal = incomeTotal.plus(parseBig(block.sums?.income_total));
        expenseTotal = expenseTotal.plus(parseBig(block.sums?.expense_total));
        revenueTotal = revenueTotal.plus(parseBig(block.sums?.revenue));
      }
      if (!acc[block.state]) {
        acc[block.state] = [];
      }
      acc[block.state].push(block);

      return acc;
    },
    {} as Record<BlockState, BlockDetails[]>,
  );

  return (
    <div className="container-fluid">
      <Box mb="1" display="flex" alignItems="center" gap="1">
        <MenuList direction="horizontal" variant="pills-inverse" size="sm">
          <MenuItem
            label="All"
            component={NavLink}
            to={planningBase}
            end
            suffix={<Badge variant="secondary" count={blocks.length} />}
          />
          {availableTypes.map((type) => (
            <MenuItem
              key={type}
              label={activityTypeLabels[type]}
              component={NavLink}
              to={`${planningBase}/type/${type}`}
              end
              prefix={<ActivityIcon type={type} size="sm" />}
              suffix={<Badge variant="secondary" count={typeCounts[type]} />}
            />
          ))}
        </MenuList>
        {manage && <Toolbar campaign={ctx.item} addBlock={add} />}
      </Box>
      <Table tableClassName="campaign-overview" fixed variant="dark" size="xs" fontSize="sm" bordered hover>
        <Thead>
          <Tr>
            <Th className="controls">{/* controls */}</Th>
            <Th className="state">{/* bound state */}</Th>
            <Th className="block">Block</Th>
            <Th className="period">Period</Th>
            <Th className="planners">Planners</Th>
            <Th textAlign="end" className="budget">
              Budget
            </Th>
            <Th textAlign="end" className="client" fontWeight="bold">
              Client total
            </Th>
            <Th textAlign="end" className="agency">
              Agency total
            </Th>
            <Th textAlign="end" className="revenue">
              Revenue
            </Th>
          </Tr>
        </Thead>
        <Thead>
          <Tr variant="light">
            <Th colSpan={5} />
            <Th textAlign="end">{fmt(budget)}</Th>
            <Th textAlign="end" className="client">
              {fmt(incomeTotal)}
            </Th>
            <Th textAlign="end" className="agency">
              {fmt(expenseTotal)}
            </Th>
            <Th textAlign="end" className="revenue">
              {fmt(revenueTotal)}
            </Th>
          </Tr>
        </Thead>

        {blockStates.map((state) => {
          const stateBlocks = groupedBlocks[state];
          if (stateBlocks) {
            return (
              <BlockGroup
                campaign={ctx.item}
                state={state}
                key={state}
                blocks={stateBlocks}
                collapse={state === 'CANCELED'}
                replace={replace}
                remove={remove}
                add={add}
                toggleLoading={ctx.setSpinning}
              />
            );
          }
          return null;
        })}
      </Table>
    </div>
  );
}
